import { ThemeLayerRenderer, type ThemeDefinitionDto } from "@stagvista/theme-renderer";
import { createFileRoute } from "@tanstack/react-router";
import * as React from "react";

import {
  DEFAULT_DISPLAY_LABELS,
  ExtendedDisplayView,
  formatDisplayTime,
} from "../components/extended-display-view";
import {
  useLiveExtendedDisplayData,
  type LiveExtendedDisplayData,
} from "../hooks/use-live-extended-display-data";
import { resolveCountdownZone } from "../lib/countdown-color-tiers";
import {
  loadDisplaySession,
  storeDisplaySession,
  type DisplaySession,
} from "../lib/display-session";
import { SidecarHttpClient, SidecarHttpError } from "../lib/sidecar-http-client";
import { SidecarWsClient } from "../lib/sidecar-ws-client";

export const Route = createFileRoute("/display/$outputId")({
  component: DisplayPage,
});

interface OutputConfigState {
  readonly overscanPercent: number;
  readonly scaleFactor: number;
  /** `role: 'browser'` rows only — absent on outputs never explicitly switched, always treated as `"themed"`. */
  readonly displayMode?: "mirror" | "themed";
}

interface OutputDto {
  readonly id: string;
  readonly themeId: string | null;
  readonly enabled: boolean;
  readonly blackout: boolean;
  readonly config: OutputConfigState;
}

interface ResolvedThemeColors {
  readonly backgroundColor: string | null;
  readonly foregroundColor: string | null;
}

const TOKEN_REFERENCE_PATTERN = /^\{([a-zA-Z][\w.]*)\}$/;

/**
 * Background/foreground for the outer `<main>` frame — identical logic
 * to `apps/desktop`'s own `routes/output.$outputId.tsx`. The theme's
 * `layers[]` themselves are drawn on top by `@stagvista/theme-renderer`'s
 * `ThemeLayerRenderer` (shared with that same desktop route), which
 * deliberately doesn't touch background either — this stays the one
 * place that reads `background`/`tokens["color.fg"]`.
 */
function resolveThemeColors(definition: ThemeDefinitionDto | null): ResolvedThemeColors {
  if (!definition) return { backgroundColor: null, foregroundColor: null };
  const tokens = definition.tokens;
  const background = definition.background;

  function resolve(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const match = TOKEN_REFERENCE_PATTERN.exec(value);
    if (match?.[1]) return tokens[match[1]] ?? null;
    return value;
  }

  return {
    backgroundColor: resolve(background?.value),
    foregroundColor: tokens["color.fg"] ? resolve("{color.fg}") : null,
  };
}

/** Same shape as `ExtendedDisplayView`'s own local `formatTimer` — a leading "-" once overtime goes negative, hours only when the duration actually has any. */
function formatTimerText(seconds: number): string {
  const sign = seconds < 0 ? "-" : "";
  const absolute = Math.abs(seconds);
  const hours = Math.floor(absolute / 3600);
  const minutes = Math.floor((absolute % 3600) / 60);
  const remainingSeconds = absolute % 60;
  const pad = (value: number) => String(value).padStart(2, "0");
  if (hours > 0) return `${sign}${hours}:${pad(minutes)}:${pad(remainingSeconds)}`;
  return `${sign}${pad(minutes)}:${pad(remainingSeconds)}`;
}

/**
 * docs/design.md §5.11/§6.4 — the actual browser-rendered display page a
 * phone/tablet loads after scanning the Browser Display Setup QR code.
 * Reached only via `packages/local-server`'s `GET /display/:outputId`
 * redirect. A `viewer`/`assistant`-role pairing session (PIN-gated,
 * identical mechanism to any other secondary controller — there is no
 * separate "display-only" credential in this codebase) is required
 * before this page can read anything; once paired, it connects directly
 * to the sidecar's own WS (no Tauri process relaying events here, unlike
 * `apps/desktop`'s output windows), filtering every `outputs`-channel
 * broadcast down to this one `outputId`.
 */
type ResolvedOutputId =
  | { readonly status: "resolving" }
  | { readonly status: "ready"; readonly outputId: string }
  | { readonly status: "error"; readonly message: string };

const SHORT_DISPLAY_NUMBER_PATTERN = /^\d+$/;

/**
 * `apps/desktop`'s own `browser-display.tsx` builds this route's real,
 * shown/copied link from a short 1-based `displayNumber` (e.g.
 * `/display/1`), not the output's actual UUID — see that file's own doc
 * comment for why. This resolves that short number back to the real id,
 * pre-auth (`GET /api/v1/outputs/resolve/:displayNumber`,
 * `delegated.route.ts`'s own doc comment — no pairing session exists yet
 * at this point), before anything below ever sees it: `PairingForm`/
 * `RenderedDisplay` are otherwise unchanged and still work with a real
 * id exactly as before. A param that isn't all-digits is assumed to
 * already be a real id (an older QR/link minted before this scheme, or
 * a UUID typed/pasted directly) and skips resolution entirely — real ids
 * here are UUIDs, which always contain at least one non-digit character.
 */
function DisplayPage(): React.ReactElement {
  const { outputId: rawOutputId } = Route.useParams();
  const [resolved, setResolved] = React.useState<ResolvedOutputId>(() =>
    SHORT_DISPLAY_NUMBER_PATTERN.test(rawOutputId)
      ? { status: "resolving" }
      : { status: "ready", outputId: rawOutputId },
  );

  React.useEffect(() => {
    if (!SHORT_DISPLAY_NUMBER_PATTERN.test(rawOutputId)) return;
    let cancelled = false;
    const client = new SidecarHttpClient(() => null);
    client
      .request<{ id: string }>("GET", `/api/v1/outputs/resolve/${rawOutputId}`)
      .then((result) => {
        if (!cancelled) setResolved({ status: "ready", outputId: result.id });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setResolved({
          status: "error",
          message:
            cause instanceof SidecarHttpError
              ? (cause.problem.detail ?? cause.problem.title)
              : "Could not reach the local server.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [rawOutputId]);

  if (resolved.status === "resolving") {
    return (
      <main style={centeredPageStyle}>
        <p style={{ opacity: 0.6 }}>Connecting…</p>
      </main>
    );
  }

  if (resolved.status === "error") {
    return (
      <main style={centeredPageStyle}>
        <h1 style={{ fontSize: 24, fontWeight: 600 }}>This link is no longer valid</h1>
        <p style={{ maxWidth: 320, textAlign: "center", color: "#ef4444" }}>{resolved.message}</p>
      </main>
    );
  }

  return <PairedOrPairing outputId={resolved.outputId} />;
}

function PairedOrPairing({ outputId }: { outputId: string }): React.ReactElement {
  const [session, setSession] = React.useState<DisplaySession | null>(() => loadDisplaySession());

  if (!session) {
    return <PairingForm onPaired={setSession} outputId={outputId} />;
  }
  return <RenderedDisplay outputId={outputId} session={session} />;
}

function PairingForm({
  onPaired,
  outputId,
}: {
  onPaired: (session: DisplaySession) => void;
  outputId: string;
}): React.ReactElement {
  const [pin, setPin] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<"idle" | "submitting" | "waiting-for-approval">("idle");

  const acceptApproval = React.useCallback(
    (approval: { token: string; role: DisplaySession["role"]; expiresAt: number }) => {
      const session: DisplaySession = {
        role: approval.role,
        token: approval.token,
        expiresAt: approval.expiresAt,
      };
      storeDisplaySession(session);
      onPaired(session);
    },
    [onPaired],
  );

  const pollUntilResolved = React.useCallback(
    (pendingRequestId: string) => {
      setPhase("waiting-for-approval");
      const client = new SidecarHttpClient(() => null);

      const poll = async (): Promise<void> => {
        try {
          const result = await client.request<
            | { status: "pending" }
            | { status: "approved"; token: string; role: DisplaySession["role"]; expiresAt: number }
            | { status: "rejected" }
            | { status: "expired" }
          >("GET", `/api/v1/pairing/pending/${pendingRequestId}/status`);
          if (result.status === "pending") {
            setTimeout(() => void poll(), 2000);
            return;
          }
          if (result.status === "approved") {
            acceptApproval(result);
            return;
          }
          setError(
            result.status === "rejected"
              ? "The operator declined this display's pairing request."
              : "This pairing request expired before it was approved — try again.",
          );
          setPhase("idle");
        } catch {
          setError("Lost contact with the local server while waiting for approval.");
          setPhase("idle");
        }
      };

      void poll();
    },
    [acceptApproval],
  );

  const submit = React.useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setPhase("submitting");
      setError(null);
      try {
        const client = new SidecarHttpClient(() => null);
        const response = await client.request<
          | { status: "approved"; token: string; role: DisplaySession["role"]; expiresAt: number }
          | { status: "pending"; pendingRequestId: string }
        >("POST", "/api/v1/pair", { pin, outputId });
        if (response.status === "approved") acceptApproval(response);
        else pollUntilResolved(response.pendingRequestId);
      } catch (cause) {
        setError(
          cause instanceof SidecarHttpError
            ? (cause.problem.detail ?? cause.problem.title)
            : "Could not reach the local server.",
        );
        setPhase("idle");
      }
    },
    [pin, outputId, acceptApproval, pollUntilResolved],
  );

  if (phase === "waiting-for-approval") {
    return (
      <main style={centeredPageStyle}>
        <h1 style={{ fontSize: 24, fontWeight: 600 }}>Waiting for approval…</h1>
        <p style={{ maxWidth: 320, textAlign: "center", opacity: 0.7 }}>
          An operator needs to approve this display from the Remote Control screen.
        </p>
        {error ? <p style={{ color: "#ef4444" }}>{error}</p> : null}
      </main>
    );
  }

  return (
    <main style={centeredPageStyle}>
      <form
        onSubmit={submit}
        style={{ display: "flex", width: 288, flexDirection: "column", gap: 12 }}
      >
        <h1 style={{ fontSize: 24, fontWeight: 600 }}>Pair this display</h1>
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          placeholder="6-digit PIN"
          maxLength={6}
          autoFocus
          style={{
            height: 44,
            borderRadius: 6,
            border: "1px solid #383e46",
            background: "#14171b",
            color: "#f2f4f7",
            padding: "0 12px",
            fontSize: 20,
            fontVariantNumeric: "tabular-nums",
          }}
        />
        <button
          type="submit"
          disabled={pin.length !== 6 || phase === "submitting"}
          style={{
            height: 44,
            borderRadius: 6,
            border: "none",
            background: "#3b82f6",
            color: "#fff",
            fontWeight: 500,
            opacity: pin.length !== 6 || phase === "submitting" ? 0.5 : 1,
          }}
        >
          {phase === "submitting" ? "Pairing…" : "Pair"}
        </button>
        {error ? <p style={{ color: "#ef4444" }}>{error}</p> : null}
      </form>
    </main>
  );
}

function RenderedDisplay({
  outputId,
  session,
}: {
  outputId: string;
  session: DisplaySession;
}): React.ReactElement {
  const [output, setOutput] = React.useState<OutputDto | null>(null);
  const [blackout, setBlackout] = React.useState(false);
  const [testPattern, setTestPattern] = React.useState(false);
  const [identifyFlash, setIdentifyFlash] = React.useState(false);
  const [themeColors, setThemeColors] = React.useState<ResolvedThemeColors>({
    backgroundColor: null,
    foregroundColor: null,
  });
  const [themeDefinition, setThemeDefinition] = React.useState<ThemeDefinitionDto | null>(null);
  const [connectionError, setConnectionError] = React.useState<string | null>(null);

  // Current/next item, message, countdown-color tiers, and a ticking
  // clock — the same live data `MirrorExtendedDisplay` already needed,
  // now also what the themed (non-mirror) branch below needs to feed
  // `ThemeLayerRenderer`. Lifted up here (instead of each branch calling
  // this hook separately) so only one WS connection for this data exists
  // regardless of which branch is actually rendering.
  const live = useLiveExtendedDisplayData(session.token, outputId);

  React.useEffect(() => {
    const http = new SidecarHttpClient(() => session.token);
    const ws = new SidecarWsClient(() => session.token, ["outputs", "timer"], outputId);

    async function fetchTheme(themeId: string | null): Promise<void> {
      if (!themeId) {
        setThemeColors({ backgroundColor: null, foregroundColor: null });
        setThemeDefinition(null);
        return;
      }
      try {
        const theme = await http.request<{ definition: ThemeDefinitionDto }>(
          "GET",
          `/api/v1/themes/${themeId}`,
        );
        setThemeColors(resolveThemeColors(theme.definition));
        setThemeDefinition(theme.definition);
      } catch {
        setThemeColors({ backgroundColor: null, foregroundColor: null });
        setThemeDefinition(null);
      }
    }

    async function loadOutput(): Promise<void> {
      try {
        const outputs = await http.request<OutputDto[]>("GET", "/api/v1/outputs");
        const match = outputs.find((o) => o.id === outputId) ?? null;
        setOutput(match);
        setBlackout(match?.blackout ?? false);
        await fetchTheme(match?.themeId ?? null);
      } catch (cause) {
        setConnectionError(
          cause instanceof SidecarHttpError
            ? (cause.problem.detail ?? cause.problem.title)
            : "Could not reach the local server.",
        );
      }
    }

    void loadOutput();
    ws.start();

    const unsubscribers = [
      ws.on("output:changed", (message) => {
        if (message.outputId !== outputId) return;
        void fetchTheme(typeof message.themeId === "string" ? message.themeId : null);
      }),
      ws.on("output:blackout", (message) => {
        if (message.outputId !== outputId && message.outputId !== "all") return;
        setBlackout(Boolean(message.on));
      }),
      ws.on("output:config", (message) => {
        if (message.outputId !== outputId) return;
        setOutput((prev) =>
          prev ? { ...prev, config: message.config as OutputConfigState } : prev,
        );
      }),
      ws.on("output:identify", (message) => {
        if (message.outputId !== outputId) return;
        setIdentifyFlash(true);
        setTimeout(() => setIdentifyFlash(false), 2000);
      }),
      ws.on("output:test_pattern", (message) => {
        if (message.outputId !== outputId) return;
        setTestPattern(Boolean(message.on));
      }),
    ];

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      ws.stop();
    };
  }, [outputId, session.token]);

  if (connectionError) {
    return (
      <main style={centeredPageStyle}>
        <h1 style={{ fontSize: 24, fontWeight: 600 }}>Could not load this display</h1>
        <p style={{ maxWidth: 320, textAlign: "center", color: "#ef4444" }}>{connectionError}</p>
      </main>
    );
  }

  if (!output) {
    return (
      <main style={centeredPageStyle}>
        <p style={{ opacity: 0.6 }}>Connecting…</p>
      </main>
    );
  }

  if (!output.enabled) {
    return (
      <main style={centeredPageStyle}>
        <h1 style={{ fontSize: 24, fontWeight: 600 }}>This display is turned off</h1>
        <p style={{ maxWidth: 320, textAlign: "center", opacity: 0.7 }}>
          An operator switched this screen to Inactive from Browser Display setup. It'll resume
          automatically once switched back to Active.
        </p>
      </main>
    );
  }

  const scaleFactor = output.config?.scaleFactor ?? 1;
  const overscanPercent = output.config?.overscanPercent ?? 0;
  const backgroundColor = themeColors.backgroundColor ?? "#000";
  const foregroundColor = themeColors.foregroundColor ?? "#fff";
  // Absent on any output never explicitly switched — treated as "themed"
  // (today's pre-existing rendering), matching `outputConfigBodySchema`'s
  // own doc comment on why this defaults rather than migrates.
  const isMirror = output.config?.displayMode === "mirror";

  // A live `timerType: "clock"` item (or no active item at all) has no
  // countdown to color or show digits for — `live.displayMode` already
  // says so (`useLiveExtendedDisplayData`'s own doc comment); this shows
  // the real wall clock instead, same as `MirrorExtendedDisplay`'s own
  // `displayMode === "clock"` branch already does.
  const isClockMode = live.displayMode === "clock";
  // An untimed count-up/session has no target to be "near" either — see
  // `ExtendedDisplayView`'s own identical guard for why `remainingSeconds`
  // alone (reported as 0, not `Infinity`) would otherwise resolve to
  // "critical" here.
  const zone =
    isClockMode || live.isUntimedCountUp
      ? "normal"
      : resolveCountdownZone(
          live.totalSeconds * 1000,
          live.remainingSeconds * 1000,
          live.countdownColorTiers,
        );
  const timerSeconds = live.isUntimedCountUp ? live.elapsedSeconds : live.remainingSeconds;
  const timerText = isClockMode
    ? formatDisplayTime(live.now, navigator.language, "12h")
    : formatTimerText(timerSeconds);

  return (
    <main
      style={{
        position: "relative",
        width: "100vw",
        height: "100vh",
        // Blackout must be true black — the theme's own background is often
        // not (a light theme's is white), so blanking only the content left
        // a bright screen behind it.
        background: isMirror || blackout ? "#000" : backgroundColor,
        color: foregroundColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "none",
        overflow: "hidden",
        userSelect: "none",
      }}
    >
      {blackout ? null : testPattern ? (
        <div
          style={{
            width: "100%",
            height: "100%",
            background:
              "repeating-linear-gradient(45deg, #f00, #f00 10%, #0f0 10%, #0f0 20%, #00f 20%, #00f 30%)",
          }}
        />
      ) : isMirror ? (
        <MirrorExtendedDisplay live={live} />
      ) : themeDefinition ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            transform: `scale(${scaleFactor})`,
            padding: `${overscanPercent}%`,
          }}
        >
          <ThemeLayerRenderer
            definition={themeDefinition}
            timerText={timerText}
            zone={zone}
            isPaused={live.statusLabel === "PAUSED"}
            clockNow={live.now}
            currentItemTitle={live.currentItem?.title ?? null}
            currentItemSpeaker={live.currentItem?.speaker ?? null}
            nextItemTitle={live.nextItem?.title ?? null}
            messageText={live.message?.text ?? null}
            messageStyle={live.message?.style ?? "fullscreen"}
            messageBlink={live.message?.flash ?? false}
            upcomingItems={live.upcomingItems}
          />
        </div>
      ) : (
        <div
          style={{
            transform: `scale(${scaleFactor})`,
            padding: `${overscanPercent}%`,
            fontSize: "12vw",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {timerText}
        </div>
      )}
      {identifyFlash ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            fontSize: "20vw",
            fontWeight: 700,
            color: "#ff0",
            textShadow: "0 0 2vw rgba(0,0,0,0.8)",
          }}
        >
          {outputId.slice(0, 2).toUpperCase()}
        </div>
      ) : null}
    </main>
  );
}

/**
 * `config.displayMode === "mirror"` branch — a byte-for-byte visual match
 * with the real Extended Display (`apps/desktop`'s
 * `components/extended-display/extended-display-view.tsx`). `live` is
 * lifted up into `RenderedDisplay` and passed down as a prop (rather
 * than this component calling `useLiveExtendedDisplayData` itself) since
 * the themed (non-mirror) branch now needs that exact same data too —
 * one WS connection either way, not two running in parallel.
 */
function MirrorExtendedDisplay({ live }: { live: LiveExtendedDisplayData }): React.ReactElement {
  return (
    <ExtendedDisplayView
      now={live.now}
      currentItem={live.currentItem}
      nextItem={live.nextItem}
      remainingSeconds={live.remainingSeconds}
      elapsedSeconds={live.elapsedSeconds}
      totalSeconds={live.totalSeconds}
      progressPercent={live.progressPercent}
      statusLabel={live.statusLabel}
      overlay={{
        visible: live.message !== null,
        text: live.message?.text ?? "",
        blink: live.message?.flash ?? false,
        style: live.message?.style ?? "fullscreen",
      }}
      blackScreen={false}
      displayMode={live.displayMode}
      preferredText={live.eventName ?? "STANDBY"}
      locale={navigator.language}
      labels={DEFAULT_DISPLAY_LABELS}
      fontFamily="Inter, sans-serif"
      fontSizeScale={1}
      clockFormat="12h"
      countdownColorTiers={live.countdownColorTiers}
      isCountUp={live.isUntimedCountUp}
    />
  );
}

const centeredPageStyle: React.CSSProperties = {
  display: "flex",
  height: "100vh",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 12,
  background: "#0b0d10",
  color: "#f2f4f7",
};
